import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Home, Euro } from 'lucide-react';

const DestinationCard = ({ destination, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardStyle = {
    transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
    boxShadow: isHovered 
      ? '0 25px 50px rgba(0, 0, 0, 0.15), 0 15px 35px rgba(212, 175, 55, 0.1)'
      : '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  return (
    <Card 
      className="relative overflow-hidden cursor-pointer border-0 shadow-none bg-white"
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(destination)}
    >
      {/* Portrait Image Container */}
      <div className="relative h-80 overflow-hidden">
        <img 
          src={destination.image}
          alt={destination.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out"
          style={{
            transform: isHovered ? 'scale(1.08)' : 'scale(1.05)',
          }}
        />
        
        {/* Elegant Gradient Overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: isHovered 
              ? 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.8) 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.75) 100%)',
            transition: 'background 0.3s ease'
          }}
        />

        {/* Villa Count Badge */}
        <div className="absolute top-4 right-4">
          <Badge 
            variant="secondary" 
            className="backdrop-blur-md bg-white/10 text-white border-white/20 px-3 py-1.5 text-xs font-light uppercase tracking-widest"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {destination.villaCount} villas
          </Badge>
        </div>

        {/* Bottom Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          {/* Location */}
          <div className="flex items-center gap-2 mb-3 opacity-90">
            <MapPin className="w-4 h-4 text-white" />
            <span 
              className="text-sm text-white font-light tracking-wide"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {destination.country}
            </span>
          </div>

          {/* Destination Name */}
          <h3 
            className="text-2xl font-light mb-3 text-white tracking-wide leading-tight"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
            }}
          >
            {destination.name}
          </h3>

          {/* Description */}
          <p 
            className="text-sm text-white/90 mb-4 font-light leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {destination.description}
          </p>

          {/* Price Range */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/80">
              <Euro className="w-4 h-4" />
              <span 
                className="text-sm font-light"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {destination.priceRange}
              </span>
            </div>
            
            {/* Subtle Explore Indicator */}
            <div 
              className={`w-2 h-2 rounded-full bg-amber-400 transition-all duration-300 ${
                isHovered ? 'scale-150 shadow-lg shadow-amber-400/50' : 'scale-100'
              }`}
            />
          </div>
        </div>
      </div>

      {/* Hover Ripple Effect */}
      {isHovered && (
        <div 
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(212, 175, 55, 0.3) 0%, transparent 70%)',
            animation: 'ripple 0.6s ease-out'
          }}
        />
      )}

      {/* Bottom Border Accent */}
      <div 
        className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-amber-600 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </Card>
  );
};

export default DestinationCard;